import { plural, t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { LockIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";

import { durationDisplayFromMinutesLong } from "../../../../../../../utils/formatTime";
import { RemoteLockCode_Booking$key } from "./__generated__/RemoteLockCode_Booking.graphql";
import { RemoteLockCode_BookingSummaryRemoteLockCodeConfig$key } from "./__generated__/RemoteLockCode_BookingSummaryRemoteLockCodeConfig.graphql";

type RemoteLockCodeProps = {
  fragmentRef: RemoteLockCode_BookingSummaryRemoteLockCodeConfig$key | null;
  bookingRef: RemoteLockCode_Booking$key | null;
};

export const RemoteLockCode = ({
  bookingRef,
  fragmentRef,
}: RemoteLockCodeProps) => {
  const data = useFragment(RemoteLockCodeFragmentString, fragmentRef);
  const booking = useFragment(RemoteLockCodeBookingFragmentString, bookingRef);

  const remoteLockAccessCode = booking?.remoteLockAccessCode;
  const courseRemoteLockId = booking?.startSlot?.course?.remoteLockId;

  //Getting notification info
  const node =
    data?.bookingNotificationSettings?.notifiableEventSettings?.edges?.[0]
      ?.node;
  const sendBeforeTimeUnit = node?.sendBeforeTimeUnit;
  const sendBeforeTimeAmount = node?.sendBeforeTimeAmount;

  const timeBeforeBookingSendNotification =
    sendBeforeTimeUnit === "MINUTE" && sendBeforeTimeAmount
      ? durationDisplayFromMinutesLong(sendBeforeTimeAmount)?.toLowerCase()
      : sendBeforeTimeUnit === "HOUR" && sendBeforeTimeAmount
      ? plural(sendBeforeTimeAmount, { one: "# hour", other: "# hours" })
      : null;

  if (!data || !booking || !courseRemoteLockId) {
    return null;
  }
  return (
    <HStack
      justifyContent="space-between"
      alignItems="center"
      padding={5}
      backgroundColor="surface-secondary"
      style={{ borderRadius: 12 }}
      marginTop={5}
      marginBottom={2}
    >
      <Box gap={2}>
        <Text
          variant="mainMicro"
          color="text-secondary"
        >{t`YOUR ACCESS CODE`}</Text>
        {remoteLockAccessCode ? (
          <Text variant="mainTitle2" color="text-primary">
            {remoteLockAccessCode}
          </Text>
        ) : (
          <Text variant="mainBaseMedium" color="text-primary">
            {timeBeforeBookingSendNotification
              ? t`You will receive your Access Code ${timeBeforeBookingSendNotification} before your booking.`
              : t`Time before booking send notification not set.`}
          </Text>
        )}
      </Box>

      <Box
        backgroundColor="grayscale-0"
        style={{ borderRadius: 20, height: 40, width: 40 }}
        justifyContent="center"
        alignItems="center"
      >
        <LockIcon color="#001A19" />
      </Box>
    </HStack>
  );
};

const RemoteLockCodeBookingFragmentString = graphql`
  fragment RemoteLockCode_Booking on Booking {
    remoteLockAccessCode
    startSlot {
      ... on TimeSlot {
        course {
          remoteLockId
        }
      }
    }
  }
`;

export const RemoteLockCodeFragmentString = graphql`
  fragment RemoteLockCode_BookingSummaryRemoteLockCodeConfig on Facility {
    bookingNotificationSettings {
      notifiableEventSettings(type: [ACCESS_CODE]) {
        edges {
          node {
            id
            type
            sendBeforeTimeAmount
            sendBeforeTimeUnit
          }
        }
      }
    }
  }
`;
